import React, { useState, useEffect } from 'react';
import { decryptData } from 'utils/crypto';
import { useParams } from 'react-router-dom';
import {
  CircularProgress,
  CircularProgressLabel,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  Box,
  Button,
  List,
  ListItem,
  useDisclosure,
  Grid,
  GridItem,
  HStack,
} from '@chakra-ui/react';
import { FiMoreHorizontal } from 'react-icons/fi';
import { FieldTimeOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import httpInjectorService from 'services/http-injector.service';
import Cookies from 'js-cookie';

const timeRegex = /^(\d+\s*(w|d|h|m)\s*)+$/;

const LogWork = ({
  isOpen: externalIsOpen,
  onClose: externalOnClose,
  work_Id,
  work_Type,
  taskDetails,
  editData = null,
  onSaved,
  showMenu = true,
}) => {
 
  const disclosure = useDisclosure();
  const isControlled = externalIsOpen !== undefined;

  const isOpen = isControlled ? externalIsOpen : disclosure.isOpen;
  const onOpen = isControlled ? () => {} : disclosure.onOpen;
  const onClose = isControlled ? externalOnClose : disclosure.onClose;

  const isEditMode = !!editData;

  const [timeSpent, setTimeSpent] = useState('');
  const [dateStarted, setDateStarted] = useState('');
  const [startTime, setStartTime] = useState('');
  const [description, setDescription] = useState('');
  const [logdata, setLogdata] = useState(null);
  const [progress, setProgress] = useState(0);
  const [liveRemaining, setLiveRemaining] = useState('');
  const { id: projectId } = useParams();

 
 
  const work_id = taskDetails?.task?.issue_id || work_Id;
  const work_type = editData?.work_type || work_Type;

  const [touched, setTouched] = useState({
    timeSpent: false,
    dateStarted: false,
    startTime: false,
  });

  const encrypted_userid = Cookies.get('user_id');
  const user_id = decryptData(encrypted_userid);

  /* ===============================
     Validation
  =============================== */

  const validateTime = (value) => {
    if (!value || !value.trim()) {
      return 'Time spent is required';
    }

    if (!timeRegex.test(value.trim())) {
      return 'Time should be in format: 2w 4d 6h 45m';
    }

    return '';
  };

  const validateDate = (value) => {
    if (!value) return 'Start date is required';
    return '';
  };

  const validateStartTime = (value) => {
    if (!value) return 'Start time is required';
    return '';
  };

  /* ===============================
     Fetch Logged Data (KEEPED)
  =============================== */

  useEffect(() => {
    if (isOpen) {
      getLogdata();
    }
  }, [isOpen]);

  const getLogdata = async () => {
    const queryParams = `?project_id=${projectId}&work_id=${work_id}&work_type=${work_type}`;
    try {
      const response = await httpInjectorService.getLoggedTime(queryParams);
      if (response.status === 'success' && response.data?.length > 0) {
        const data = response.data[0];
        setLogdata(data);
      } else {
        setLogdata(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formatToIST = (epoch) => {
  if (!epoch) return "--";
  return new Date(epoch * 1000).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

  useEffect(() => {
    if (!logdata?.start_time_epoch || !logdata?.end_time_epoch) return;

    const interval = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      const total = logdata.end_time_epoch - logdata.start_time_epoch;
      const remaining = logdata.end_time_epoch - now;
      const elapsed = total - remaining;

      const percent = (elapsed / total) * 100;
      setProgress(Math.min(Math.max(percent, 0), 100));

      if (remaining <= 0) {
        setLiveRemaining('Expired');
        setProgress(100);
        clearInterval(interval);
        return;
      }

      const days = Math.floor(remaining / (24 * 3600));
      const hours = Math.floor((remaining % (24 * 3600)) / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      const seconds = remaining % 60;

      setLiveRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    }, 1000);

    return () => clearInterval(interval);
  }, [logdata]);

  /* ===============================
     Prefill When Editing
  =============================== */

  useEffect(() => {
    if (isOpen) {
      setTouched({
        timeSpent: false,
        dateStarted: false,
        startTime: false,
      });

      if (isOpen && isEditMode && editData) {
        setTimeSpent(editData.duration_input || '');

        if (editData.start_time_epoch) {
          const d = new Date(editData.start_time_epoch * 1000);
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');

          const hours = String(d.getHours()).padStart(2, '0');
          const minutes = String(d.getMinutes()).padStart(2, '0');

          setDateStarted(`${yyyy}-${mm}-${dd}`);
          setStartTime(`${hours}:${minutes}`);
        }

        setDescription(editData.description || '');
      }

      if (isOpen && !isEditMode) {
        setTimeSpent('');
        setDateStarted('');
        setStartTime('');
        setDescription('');
      }
    }
  }, [isOpen, editData]);

  

  const handleSave = async () => {
    setTouched({
      timeSpent: true,
      dateStarted: true,
      startTime: true,
    });

    if (
      !timeSpent.trim() ||
      validateTime(timeSpent) ||
      !dateStarted ||
      !startTime
    ) {
      return;
    }

    const payload = {
      work_id: work_Id,
      work_type: work_Type,
      logged_time: timeSpent,
      start_date: dateStarted,
      start_time: startTime,
      project_id: projectId,
      description,
      user_id,
      reporter: taskDetails?.created_by,
    };

    // If edit mode, attach log_id
    if (isEditMode && editData?.log_id) {
      payload.log_id = editData.log_id;
    }

    try {
      let response;
      if (payload.log_id) {
        response = await httpInjectorService.updateLogTime(payload);
      } else {
        response = await httpInjectorService.timeTracking(payload);
      }
      if (response.status === 'success') {
        onClose();
        if(typeof onSaved === 'function') {
          onSaved();
        }
      }
    } catch (err) {
      console.error('Failed to save work log', err);
    }
  };

  /* ===============================
     UI (UNCHANGED)
  =============================== */

  return (
    <>
      {showMenu && (
        <Menu>
          <MenuButton
            as={IconButton}
            icon={<FiMoreHorizontal />}
            size="md"
            variant="ghost"
          />
          <MenuList p={1} minW="120px">
            <MenuItem onClick={onOpen} fontSize="sm" py={1} px={2} minH="30px">
              {isEditMode ? 'Edit Work Log' : 'Log Work'}
            </MenuItem>
          </MenuList>
        </Menu>
      )}

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isEditMode ? 'Edit Logged Time' : 'Time Tracking'}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={5} align="stretch">
              {logdata && (
                <Box bg="gray.50" p={3} borderRadius="md">
                  <HStack justify="center" spacing={2}>
                    <Text fontWeight="semibold">
                      <FieldTimeOutlined
                        style={{ fontSize: '20px', marginRight: '8px' }}
                      />
                      Logged Time: {logdata.total_duration_readable}
                    </Text>
                  </HStack>
                </Box>
              )}
              <Grid templateColumns="1fr 1fr" columnGap={6}>
                {/* LEFT SIDE */}
                <GridItem>
                  <FormControl
                    isInvalid={touched.timeSpent && !!validateTime(timeSpent)}
                  >
                    <FormLabel fontSize="sm">Time spent</FormLabel>
                    <Input
                      value={timeSpent}
                      onChange={(e) => {
                        setTimeSpent(e.target.value);
                        setTouched((t) => ({ ...t, timeSpent: true }));
                      }}
                    />
                    <Text fontSize="sm" color="red.500" minH="18px">
                      {touched.timeSpent ? validateTime(timeSpent) : ''}
                    </Text>
                  </FormControl>

                  <Box fontSize="sm" color="gray.600" mt={3}>
                    <Text mb={1}>Use the format: 2w 4d 6h 45m</Text>
                    <List pl={4} styleType="disc">
                      <ListItem>w = weeks</ListItem>
                      <ListItem>d = days</ListItem>
                      <ListItem>h = hours</ListItem>
                      <ListItem>m = minutes</ListItem>
                    </List>
                  </Box>
                </GridItem>

                {/* RIGHT SIDE */}
                <GridItem>
                  {editData && (
                    <VStack spacing={3} align="center">
                      <CircularProgress
                        value={progress}
                        size="120px"
                        thickness="6px"
                        color="#884b9e"
                      >
                        <CircularProgressLabel>
                          <Text fontSize="xs" fontWeight="bold">
                            {liveRemaining ||
                              logdata?.remaining_time_readable ||
                              '--'}
                          </Text>
                        </CircularProgressLabel>
                      </CircularProgress>

                      <Text fontSize="sm" color="gray.500">
                      Ends at: {formatToIST(logdata?.end_time_epoch)}
                      </Text>
                    </VStack>
                  )}
                </GridItem>
              </Grid>

              <Grid templateColumns="1fr 1fr" columnGap={2}>
                <FormControl
                  isInvalid={touched.dateStarted && !!validateDate(dateStarted)}
                >
                  <FormLabel fontSize="sm">Date started</FormLabel>
                  <Input
                    type="date"
                    value={dateStarted}
                    onChange={(e) => setDateStarted(e.target.value)}
                    onBlur={() =>
                      setTouched((t) => ({ ...t, dateStarted: true }))
                    }
                  />
                  <Text fontSize="sm" color="red.500" minH="18px">
                    {touched.dateStarted ? validateDate(dateStarted) : ''}
                  </Text>
                </FormControl>

                <FormControl
                  isInvalid={
                    touched.startTime && !!validateStartTime(startTime)
                  }
                >
                  <FormLabel fontSize="sm">Time</FormLabel>
                  <Input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    onBlur={() =>
                      setTouched((t) => ({ ...t, startTime: true }))
                    }
                  />
                  <Text fontSize="sm" color="red.500" minH="18px">
                    {touched.startTime ? validateStartTime(startTime) : ''}
                  </Text>
                </FormControl>
              </Grid>

              <FormControl>
                <FormLabel fontSize="sm">Work description</FormLabel>
                <Box
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <ReactQuill
                    theme="snow"
                    value={description}
                    onChange={setDescription}
                    style={{ minHeight: '140px' }}
                  />
                </Box>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="ghost" onClick={handleSave}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default LogWork;
